import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A reusable accessible modal, factored from the onboarding "Merge into…" picker
 * so every dialog in the app shares one behaviour (clarity-orders-detail): a
 * `role="dialog"` + `aria-modal` surface portalled to `<body>`, an opaque
 * backdrop that closes on a bare mousedown, Escape to close, and a Tab focus
 * trap that cycles within the dialog. Focus moves to the dialog's first
 * focusable node on open; the CALLER restores focus to its trigger on close
 * (the trigger lives in the caller's tree).
 *
 * The title is rendered by the modal (wired to `aria-labelledby`); the body is
 * the caller's `children`. `onClose` fires for Escape, the backdrop, and any
 * close affordance the caller renders inside.
 */
const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

export function Modal({
  title,
  onClose,
  children,
  className,
  labelId,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Extra class on the dialog surface (defaults to the shared `.modal` chrome). */
  className?: string;
  /** Override the generated title id (so a caller can describe content elsewhere). */
  labelId?: string;
}) {
  const generatedId = useId();
  const titleId = labelId ?? generatedId;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on open (first focusable, else the dialog itself).
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;
    const first = node.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node).focus();
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div
      className="modal__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={className ?? 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="modal__head">
          <h3 id={titleId} className="modal__title">
            {title}
          </h3>
          <button
            type="button"
            className="ghost modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="modal__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
